/**
 * Voice heading-matching engine.
 *
 * SINGLE source of truth for "go to …" navigation: given a spoken section
 * name, which document heading is the best match?
 *
 *   detectVoiceCommandAtStart (navigation)  ──► findMatchingHeading (this file)
 *   selectContentUnderHeader (editor)       ──► findMatchingHeading (this file)
 *
 * Wrappers in `voiceCommands.js` / `useRteWithSpeech.js` only strip the
 * "go to" trigger and pass the tail — scoring and ranking live here.
 *
 * Design (aligned with `voiceTemplateEngine.js`):
 *   - Case-insensitive; compact + token forms.
 *   - Whole-string and per-word fuzzy similarity.
 *   - Substring boost ("comparison" → "COMPARISONS", "Tech COMPARISONS").
 *   - Top score wins; small gap → prefer shorter heading title.
 */

import { compactStringSimilarity } from "./voiceCommandMatching";

// ─────────────────────────────────────────────────────────────────────────────
// Thresholds
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum score to accept a heading (STT plural / suffix tolerance). */
export const MIN_HEADING_MATCH_SCORE = 0.68;
export const MIN_HEADING_SCORE_GAP = 0.05;

// ─────────────────────────────────────────────────────────────────────────────
// Normalization
// ─────────────────────────────────────────────────────────────────────────────

/** Collapse spaces/punctuation; case-insensitive. */
export const normalizeHeaderText = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[:–—\s]+/g, " ")
    .trim();

/** Remove spaces/hyphens for compact fuzzy match; case-insensitive. */
export const normalizeHeaderTextNoSpaces = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[\s\-‐‑‒–—―:–—]+/g, "")
    .trim();

/** Normalized spoken target (with / without spaces). */
export function getSpokenHeaderForms(headerName) {
  return {
    withSpaces: normalizeHeaderText(headerName),
    compact: normalizeHeaderTextNoSpaces(headerName),
  };
}

/** Both normalized forms for a document heading (debug). */
export function getHeadingMatchVariants(headingText) {
  return {
    withSpaces: normalizeHeaderText(headingText),
    noSpaces: normalizeHeaderTextNoSpaces(headingText),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoring
// ─────────────────────────────────────────────────────────────────────────────

const wordMatchesSpokenToken = (headingWord, spokenWord) => {
  const hw = normalizeHeaderTextNoSpaces(headingWord);
  const sw = normalizeHeaderTextNoSpaces(spokenWord);
  if (!hw || !sw) return false;
  if (hw === sw || hw.includes(sw) || sw.includes(hw)) return true;
  return compactStringSimilarity(hw, sw) >= 0.82;
};

/**
 * Score how well a document heading matches the spoken target (0–1, higher is better).
 */
export function scoreHeadingAgainstSpoken(headingText, spokenNorm, spokenCompact) {
  const hNorm = normalizeHeaderText(headingText);
  const hCompact = normalizeHeaderTextNoSpaces(headingText);
  if (!spokenCompact || !hCompact) return 0;

  if (hNorm === spokenNorm || hCompact === spokenCompact) {
    return 1;
  }

  let best = compactStringSimilarity(spokenCompact, hCompact);

  if (hCompact.includes(spokenCompact) || spokenCompact.includes(hCompact)) {
    best = Math.max(best, 0.9, compactStringSimilarity(spokenCompact, hCompact));
  }

  const spokenWords = spokenNorm.split(/\s+/).filter(Boolean);
  const headingWords = hNorm.split(/\s+/).filter(Boolean);

  if (spokenWords.length > 0 && headingWords.length > 0) {
    let matched = 0;
    for (const sw of spokenWords) {
      if (headingWords.some((hw) => wordMatchesSpokenToken(hw, sw))) {
        matched += 1;
      }
    }
    const coverage = matched / spokenWords.length;
    if (coverage >= 0.5) {
      best = Math.max(best, 0.72 + 0.26 * coverage);
    }

    if (spokenWords.length >= 2 && headingWords.length >= spokenWords.length) {
      for (let i = 0; i <= headingWords.length - spokenWords.length; i += 1) {
        const window = headingWords.slice(i, i + spokenWords.length).join(" ");
        const windowCompact = normalizeHeaderTextNoSpaces(window);
        best = Math.max(
          best,
          compactStringSimilarity(spokenCompact, windowCompact),
        );
      }
    }
  }

  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rank all headings and return the best match (or null).
 * @param {string} spokenHeaderName - Text after "go to" (all languages)
 * @param {Array<{ text: string, pos?: number, level?: number, node?: object }>} headings
 * @param {{ minScore?: number, minGap?: number }} [opts]
 * @returns {{ heading: object, score: number } | null}
 */
export function findBestHeading(spokenHeaderName, headings, opts = {}) {
  if (!spokenHeaderName || !headings || headings.length === 0) return null;

  const minScore = opts.minScore ?? MIN_HEADING_MATCH_SCORE;
  const minGap = opts.minGap ?? MIN_HEADING_SCORE_GAP;

  const { withSpaces: spokenNorm, compact: spokenCompact } =
    getSpokenHeaderForms(spokenHeaderName);

  const ranked = headings
    .map((heading) => ({
      heading,
      score: scoreHeadingAgainstSpoken(
        heading.text,
        spokenNorm,
        spokenCompact,
      ),
    }))
    .filter((row) => row.score >= minScore)
    .sort((a, b) => b.score - a.score);

  if (ranked.length === 0) return null;

  if (ranked.length >= 2 && ranked[0].score - ranked[1].score < minGap) {
    ranked.sort(
      (a, b) =>
        normalizeHeaderTextNoSpaces(a.heading.text).length -
        normalizeHeaderTextNoSpaces(b.heading.text).length,
    );
  }

  return { heading: ranked[0].heading, score: ranked[0].score };
}

/**
 * Best matching heading node for editor navigation (convenience wrapper).
 * @param {string} [_language] - Ignored; matching is language-agnostic fuzzy scoring.
 * @returns {object|null} heading entry from `headings` array
 */
export function findMatchingHeading(headerName, _language, headings, opts) {
  return findBestHeading(headerName, headings, opts)?.heading ?? null;
}
